import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { PAGE_COLORS } from '../../utils/pageColors';
import { useCreateQuoteMutation } from '../../store/slices/apiSlice';
import { FileText, ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
import type { QuoteFormData, QuoteItem } from '../../types/quotes';

const schema = z.object({
  customer_first_name: z.string().min(1, 'First name required'),
  customer_last_name: z.string().min(1, 'Last name required'),
  customer_business_name: z.string().optional().or(z.literal('')),
  customer_email: z.string().email().optional().or(z.literal('')),
  customer_mobile_number: z.string().optional().or(z.literal('')),
  
  invoice_address_line1: z.string().min(1, 'Invoice address required'),
  invoice_address_line2: z.string().optional().or(z.literal('')),
  invoice_postcode: z.string().min(1, 'Invoice postcode required'),
  
  delivery_address_line1: z.string().min(1, 'Delivery address required'),
  delivery_address_line2: z.string().optional().or(z.literal('')),
  delivery_postcode: z.string().min(1, 'Delivery postcode required'),
});

export const QuoteCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [createQuote, { isLoading }] = useCreateQuoteMutation();
  const [copyInvoiceToDelivery, setCopyInvoiceToDelivery] = useState(false);
  const [items, setItems] = useState<QuoteItem[]>([
    { item_number: 1, description: '', subtotal: 0, vat_amount: 0, total: 0 }
  ]);

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<QuoteFormData>({
    resolver: zodResolver(schema),
  });

  // Watch invoice address fields
  const invoiceLine1 = watch('invoice_address_line1');
  const invoiceLine2 = watch('invoice_address_line2');
  const invoicePostcode = watch('invoice_postcode');

  // Auto-copy invoice to delivery when checkbox checked
  useEffect(() => {
    if (copyInvoiceToDelivery) {
      setValue('delivery_address_line1', invoiceLine1 || '');
      setValue('delivery_address_line2', invoiceLine2 || '');
      setValue('delivery_postcode', invoicePostcode || '');
    }
  }, [copyInvoiceToDelivery, invoiceLine1, invoiceLine2, invoicePostcode, setValue]);

  const addItem = () => {
    setItems([...items, {
      item_number: items.length + 1,
      description: '',
      subtotal: 0,
      vat_amount: 0,
      total: 0
    }]);
  };

  const removeItem = (index: number) => {
    if (items.length === 1) {
      toast.warning('Warning', 'At least one item required');
      return;
    }
    const newItems = items.filter((_, i) => i !== index);
    // Renumber items
    newItems.forEach((item, i) => item.item_number = i + 1);
    setItems(newItems);
  };

  const updateItem = (index: number, field: keyof QuoteItem, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Recalculate VAT and total if subtotal changed
    if (field === 'subtotal') {
      const subtotal = parseFloat(value) || 0;
      newItems[index].vat_amount = subtotal * 0.20;  // 20% VAT
      newItems[index].total = subtotal + newItems[index].vat_amount;
    }
    
    setItems(newItems);
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
    const vatTotal = items.reduce((sum, item) => sum + (item.vat_amount || 0), 0);
    const total = subtotal + vatTotal;
    return { subtotal, vatTotal, total };
  };

  const onSubmit = async (data: QuoteFormData) => {
    // Validate items
    if (items.some(item => !item.description.trim())) {
      toast.error('Error', 'All items must have a description');
      return;
    }
    
    if (items.some(item => item.subtotal <= 0)) {
      toast.error('Error', 'All items must have a subtotal greater than 0');
      return;
    }
    
    try {
      const payload = {
        ...data,
        items: items.map(item => ({
          item_number: item.item_number,
          description: item.description,
          subtotal: item.subtotal,
        }))
      };
      
      const result = await createQuote(payload).unwrap();
      toast.success('Success', `Quote ${result.quote_number} created! PDF generated.`);
      navigate('/quotes');
    } catch (error: any) {
      const errorMsg = error?.data?.detail || error?.data?.message || 'Failed to create quote';
      toast.error('Error', errorMsg);
    }
  };

  const totals = calculateTotals();

  return (
    <AppShell>
      <PageLayout
        title="Create Quote"
        subtitle="Create a new quotation"
        bgColor={PAGE_COLORS.orders.bgColor}
        textColor={PAGE_COLORS.orders.textColor}
        borderColor={PAGE_COLORS.orders.borderColor}
        icon={<FileText className="w-6 h-6" />}
      >
        <div className="mb-4">
          <Link to="/quotes">
            <Button variant="secondary" icon={<ArrowLeft className="w-4 h-4" />}>
              Back to Quotes
            </Button>
          </Link>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Customer Details */}
          <ContentCard className="mb-6">
            <h3 className="text-lg font-semibold mb-4">Customer Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 font-medium">First Name *</label>
                <input {...register('customer_first_name')} className="w-full px-3 py-2 border rounded" />
                {errors.customer_first_name && <p className="text-red-600 text-sm mt-1">{errors.customer_first_name.message}</p>}
              </div>
              <div>
                <label className="block mb-1 font-medium">Last Name *</label>
                <input {...register('customer_last_name')} className="w-full px-3 py-2 border rounded" />
                {errors.customer_last_name && <p className="text-red-600 text-sm mt-1">{errors.customer_last_name.message}</p>}
              </div>
              <div>
                <label className="block mb-1 font-medium">Business Name</label>
                <input {...register('customer_business_name')} className="w-full px-3 py-2 border rounded" />
              </div>
              <div>
                <label className="block mb-1 font-medium">Email</label>
                <input {...register('customer_email')} type="email" className="w-full px-3 py-2 border rounded" />
                {errors.customer_email && <p className="text-red-600 text-sm mt-1">{errors.customer_email.message}</p>}
              </div>
              <div>
                <label className="block mb-1 font-medium">Mobile Number</label>
                <input {...register('customer_mobile_number')} className="w-full px-3 py-2 border rounded" />
              </div>
            </div>
          </ContentCard>

          {/* Invoice Address */}
          <ContentCard className="mb-6">
            <h3 className="text-lg font-semibold mb-4">Invoice Address</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block mb-1 font-medium">Address Line 1 *</label>
                <input {...register('invoice_address_line1')} className="w-full px-3 py-2 border rounded" />
                {errors.invoice_address_line1 && <p className="text-red-600 text-sm mt-1">{errors.invoice_address_line1.message}</p>}
              </div>
              <div className="col-span-2">
                <label className="block mb-1 font-medium">Address Line 2</label>
                <input {...register('invoice_address_line2')} className="w-full px-3 py-2 border rounded" />
              </div>
              <div>
                <label className="block mb-1 font-medium">Postcode *</label>
                <input {...register('invoice_postcode')} className="w-full px-3 py-2 border rounded" />
                {errors.invoice_postcode && <p className="text-red-600 text-sm mt-1">{errors.invoice_postcode.message}</p>}
              </div>
            </div>
            
            <div className="mt-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={copyInvoiceToDelivery}
                  onChange={(e) => setCopyInvoiceToDelivery(e.target.checked)}
                  className="mr-2 w-4 h-4"
                />
                <span className="text-sm">Copy invoice address to delivery address</span>
              </label>
            </div>
          </ContentCard>

          {/* Delivery Address */}
          <ContentCard className="mb-6">
            <h3 className="text-lg font-semibold mb-4">Delivery Address</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block mb-1 font-medium">Address Line 1 *</label>
                <input {...register('delivery_address_line1')} className="w-full px-3 py-2 border rounded" />
                {errors.delivery_address_line1 && <p className="text-red-600 text-sm mt-1">{errors.delivery_address_line1.message}</p>}
              </div>
              <div className="col-span-2">
                <label className="block mb-1 font-medium">Address Line 2</label>
                <input {...register('delivery_address_line2')} className="w-full px-3 py-2 border rounded" />
              </div>
              <div>
                <label className="block mb-1 font-medium">Postcode *</label>
                <input {...register('delivery_postcode')} className="w-full px-3 py-2 border rounded" />
                {errors.delivery_postcode && <p className="text-red-600 text-sm mt-1">{errors.delivery_postcode.message}</p>}
              </div>
            </div>
          </ContentCard>

          {/* Quote Items */}
          <ContentCard className="mb-6">
            <h3 className="text-lg font-semibold mb-4">Quote Items</h3>
            {items.map((item, index) => (
              <div key={index} className="mb-4 p-4 border rounded bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-medium">Item {item.item_number}</h4>
                  {items.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="text-red-600 hover:text-red-800"
                      title="Remove item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <div className="col-span-2">
                    <label className="block mb-1 text-sm font-medium">Description *</label>
                    <textarea
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      className="w-full px-3 py-2 border rounded"
                      rows={2}
                      placeholder="Enter item description..."
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium">Subtotal (£) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.subtotal || ''}
                      onChange={(e) => updateItem(index, 'subtotal', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border rounded"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium">VAT (20%)</label>
                    <input
                      type="text"
                      value={`£${item.vat_amount.toFixed(2)}`}
                      disabled
                      className="w-full px-3 py-2 border rounded bg-gray-200 text-gray-700"
                    />
                  </div>
                </div>
                <div className="mt-2 text-right">
                  <span className="font-medium text-gray-700">Item Total: £{item.total.toFixed(2)}</span>
                </div>
              </div>
            ))}
            
            <Button
              type="button"
              onClick={addItem}
              variant="secondary"
              icon={<Plus className="w-4 h-4" />}
              className="mt-2"
            >
              Add Item
            </Button>

            {/* Totals */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-semibold mb-3 text-gray-800">Quote Totals</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-gray-700">
                  <span>Total Subtotal:</span>
                  <span className="font-medium">£{totals.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Total VAT (20%):</span>
                  <span className="font-medium">£{totals.vatTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-gray-900 border-t border-blue-300 pt-2 mt-2">
                  <span>Grand Total:</span>
                  <span>£{totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </ContentCard>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-4">
            <Link to="/quotes">
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </Link>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? 'Creating Quote...' : 'Create Quote'}
            </Button>
          </div>
        </form>
      </PageLayout>
    </AppShell>
  );
};
